import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    closeDetailPanel,
    setSelectedOrganizer,
    toggleEditModal,
    setEditedOrganizer,
    toggleNeedRefresh,
} from '../../../store/organizersSlice';
import { ReactComponent as ArrowLeftIcon } from '../../icons/arrow-left.svg';
import { ReactComponent as ArrowRightIcon } from '../../icons/arrow-right.svg';
import { ReactComponent as ChartIcon } from '../../icons/chart.svg';
import { ReactComponent as TrashIcon } from '../../icons/trash.svg';
import { ReactComponent as EditIcon } from '../../icons/edit.svg';
import { ReactComponent as CheckIcon } from '../../icons/tick-circle.svg';
import { ReactComponent as CloseCircleIcon } from '../../icons/close-circle.svg';
import ApiService from '../../api_services/apiService.jsx';
import {
    hasOrganizerPendingDecision,
    isOrganizerPending,
    normalizeOrganizerProfile,
    normalizePendingOrganizerChange,
    parsePendingOrganizerPayload,
    resolveOrganizerId,
} from '../../../tables/organizers/organizersTableService.js';
import './OrganizerDetailPanel.css';

const REVIEW_FIELDS = [
    'nip',
    'companyName',
    'street',
    'city',
    'postalCode',
    'contactEmail',
    'contactPhone',
    'description',
    'socialLink',
    'siteLink',
];

function OrganizerDetailPanel({ allOrganizers = [] }) {
    const dispatch = useDispatch();
    const selectedOrganizer = useSelector((state) => state.organizers.selectedOrganizer);
    const detailPanelOpen = useSelector((state) => state.organizers.detailPanelOpen);
    const selectedOrganizerId = resolveOrganizerId(selectedOrganizer);
    const isPendingOrganizer = isOrganizerPending(selectedOrganizer);
    const isPendingDataChange = !isPendingOrganizer
        && (selectedOrganizer?.hasPendingChange === true || selectedOrganizer?.pendingType === 'data_change');
    const requiresReview = hasOrganizerPendingDecision(selectedOrganizer);

    const [activeTab, setActiveTab] = useState('basic');
    const [pendingChangePayload, setPendingChangePayload] = useState({});
    const [isDecisionLoading, setIsDecisionLoading] = useState(false);

    useEffect(() => {
        let isMounted = true;

        const fetchOrganizerProfile = async () => {
            if (!selectedOrganizerId) return;

            try {
                const response = await ApiService.getOrganiserProfile(selectedOrganizerId);
                const profile = normalizeOrganizerProfile(response?.data ?? response);
                if (isMounted && profile?.id) {
                    dispatch(setSelectedOrganizer(profile));
                }
            } catch (error) {
                console.error('Error fetching organizer profile:', error);
            }
        };

        fetchOrganizerProfile();

        return () => {
            isMounted = false;
        };
    }, [selectedOrganizerId, dispatch]);

    useEffect(() => {
        let isMounted = true;

        const fetchPendingChange = async () => {
            if (!selectedOrganizerId || !isPendingDataChange) {
                setPendingChangePayload({});
                return;
            }

            const localPayload = selectedOrganizer?.pendingChangePayload
                || parsePendingOrganizerPayload(selectedOrganizer?.pendingChange);

            if (localPayload && Object.keys(localPayload).length > 0) {
                setPendingChangePayload(localPayload);
                return;
            }

            try {
                const response = await ApiService.getPendingOrganiserChanges();
                const pendingItems = Array.isArray(response) ? response : [];
                const pendingItem = pendingItems
                    .map(normalizePendingOrganizerChange)
                    .find((item) => String(item.organiserId) === String(selectedOrganizerId));

                if (isMounted) {
                    setPendingChangePayload(pendingItem?.payload || {});
                }
            } catch (error) {
                console.error('Error fetching organizer pending changes:', error);
                if (isMounted) {
                    setPendingChangePayload({});
                }
            }
        };

        fetchPendingChange();

        return () => {
            isMounted = false;
        };
    }, [selectedOrganizerId, isPendingDataChange, selectedOrganizer?.pendingChange, selectedOrganizer?.pendingChangePayload]);

    const currentIndex = allOrganizers.findIndex((org) => String(resolveOrganizerId(org)) === String(selectedOrganizerId));
    const totalCount = allOrganizers.length;

    const rawProfile = useMemo(() => {
        return selectedOrganizer?._rawProfileEntry?.organiserProfileResponseDto
            || selectedOrganizer?._rawProfileEntry?.organizerProfileResponseDto
            || selectedOrganizer?._rawProfileEntry
            || {};
    }, [selectedOrganizer]);

    const addressParts = useMemo(() => {
        const street = rawProfile.street || selectedOrganizer?.street || '';
        const postalCode = rawProfile.postalCode || selectedOrganizer?.postalCode || selectedOrganizer?.zipCode || '';
        const city = rawProfile.city || selectedOrganizer?.city || '';

        if (street || postalCode || city) {
            return {
                street: street || '-',
                zipCode: postalCode || '-',
                city: city || '-',
            };
        }

        return getAddressParts(selectedOrganizer?.address);
    }, [rawProfile, selectedOrganizer]);

    const getBaseValue = useCallback((field) => {
        const values = {
            nip: rawProfile.nip ?? selectedOrganizer?.nip,
            companyName: rawProfile.companyName ?? selectedOrganizer?.companyName,
            street: rawProfile.street ?? addressParts.street,
            city: rawProfile.city ?? addressParts.city,
            postalCode: rawProfile.postalCode ?? addressParts.zipCode,
            contactEmail: rawProfile.contactEmail ?? selectedOrganizer?.email,
            contactPhone: rawProfile.contactPhone ?? selectedOrganizer?.phone,
            description: rawProfile.description ?? selectedOrganizer?.description,
            socialLink: rawProfile.socialLink ?? selectedOrganizer?.facebookUrl,
            siteLink: rawProfile.siteLink ?? selectedOrganizer?.websiteUrl,
        };

        return normalizeDisplayValue(values[field]);
    }, [rawProfile, selectedOrganizer, addressParts]);

    const isFieldChanged = useCallback((field) => {
        return Object.prototype.hasOwnProperty.call(pendingChangePayload || {}, field);
    }, [pendingChangePayload]);

    const getDisplayValue = useCallback((field) => {
        if (isFieldChanged(field)) {
            return normalizeDisplayValue(pendingChangePayload[field]);
        }

        return getBaseValue(field);
    }, [getBaseValue, isFieldChanged, pendingChangePayload]);

    const changedFieldsCount = REVIEW_FIELDS.filter(isFieldChanged).length;

    const handlePrevious = useCallback(() => {
        if (currentIndex > 0) {
            dispatch(setSelectedOrganizer(allOrganizers[currentIndex - 1]));
        }
    }, [currentIndex, allOrganizers, dispatch]);

    const handleNext = useCallback(() => {
        if (currentIndex < allOrganizers.length - 1) {
            dispatch(setSelectedOrganizer(allOrganizers[currentIndex + 1]));
        }
    }, [currentIndex, allOrganizers, dispatch]);

    const handleClose = useCallback(() => {
        dispatch(closeDetailPanel());
    }, [dispatch]);

    const handleEdit = useCallback(() => {
        if (selectedOrganizer) {
            dispatch(setEditedOrganizer(selectedOrganizer));
            dispatch(toggleEditModal());
        }
    }, [selectedOrganizer, dispatch]);

    const handleDelete = useCallback(() => {
        if (selectedOrganizer) {
            if (window.confirm(`Czy na pewno chcesz usunac organizatora "${selectedOrganizer.companyName}"?`)) {
                console.log('Delete organizer:', selectedOrganizerId);
                dispatch(closeDetailPanel());
            }
        }
    }, [selectedOrganizer, selectedOrganizerId, dispatch]);

    const handlePasswordReset = useCallback(() => {
        const email = getDisplayValue('contactEmail');
        if (selectedOrganizer && email && email !== '-') {
            if (window.confirm(`Czy na pewno chcesz wyslac wiadomosc z resetem hasla do "${email}"?`)) {
                ApiService.requestPasswordRecovery(email)
                    .then(() => {
                        alert('Wyslano wiadomosc z resetem hasla.');
                    })
                    .catch((error) => {
                        console.error('Password reset request failed:', error);
                    });
            }
        }
    }, [selectedOrganizer, getDisplayValue]);

    const handleStatistics = useCallback(() => {
        console.log('Show statistics for organizer:', selectedOrganizerId);
    }, [selectedOrganizerId]);

    const promptRejectReason = useCallback(() => {
        const value = window.prompt('Podaj powod odrzucenia:');
        if (value === null) {
            return null;
        }

        const reason = value.trim();
        if (!reason) {
            alert('Powod odrzucenia jest wymagany.');
            return null;
        }

        return reason;
    }, []);

    const refreshOrganizerAfterDecision = useCallback(async () => {
        if (!selectedOrganizerId) return;

        const response = await ApiService.getOrganiserProfile(selectedOrganizerId);
        const profile = normalizeOrganizerProfile(response?.data ?? response);
        if (profile?.id) {
            dispatch(setSelectedOrganizer(profile));
        }
    }, [selectedOrganizerId, dispatch]);

    const handleAcceptPending = useCallback(async () => {
        if (!selectedOrganizerId || !requiresReview) return;

        try {
            setIsDecisionLoading(true);
            if (isPendingDataChange) {
                await ApiService.approvePendingOrganiserChange(selectedOrganizerId);
            } else {
                await ApiService.approveOrganiser(selectedOrganizerId);
            }
            await refreshOrganizerAfterDecision();
            dispatch(toggleNeedRefresh());
        } catch (error) {
            console.error('Error accepting organizer review:', error);
        } finally {
            setIsDecisionLoading(false);
        }
    }, [selectedOrganizerId, requiresReview, isPendingDataChange, refreshOrganizerAfterDecision, dispatch]);

    const handleRejectPending = useCallback(async () => {
        if (!selectedOrganizerId || !requiresReview) return;

        const reason = promptRejectReason();
        if (!reason) return;

        try {
            setIsDecisionLoading(true);
            if (isPendingDataChange) {
                await ApiService.rejectPendingOrganiserChange({ id: selectedOrganizerId, reason });
                await refreshOrganizerAfterDecision();
            } else {
                await ApiService.rejectOrganiser({ id: selectedOrganizerId, reason });
                dispatch(closeDetailPanel());
            }
            dispatch(toggleNeedRefresh());
        } catch (error) {
            console.error('Error rejecting organizer review:', error);
        } finally {
            setIsDecisionLoading(false);
        }
    }, [selectedOrganizerId, requiresReview, isPendingDataChange, promptRejectReason, refreshOrganizerAfterDecision, dispatch]);

    if (!detailPanelOpen || !selectedOrganizer) {
        return null;
    }

    const reviewTitle = isPendingDataChange ? 'Zmiana danych' : isPendingOrganizer ? 'Nowe konto' : null;

    return (
        <>
            <div className="organizer-detail-backdrop" onClick={handleClose} />

            <div className="organizer-detail-panel">
                <div className="organizer-detail-header">
                    <div className="header-left">
                        <div className="navigation-arrows">
                            <button
                                className="nav-arrow-btn"
                                onClick={handlePrevious}
                                disabled={currentIndex <= 0}
                                title="Poprzedni"
                                type="button"
                            >
                                <ArrowLeftIcon className="nav-arrow-icon" />
                            </button>
                            <button
                                className="nav-arrow-btn"
                                onClick={handleNext}
                                disabled={currentIndex >= totalCount - 1}
                                title="Nastepny"
                                type="button"
                            >
                                <ArrowRightIcon className="nav-arrow-icon" />
                            </button>
                        </div>
                        <span className="results-counter">
                            {currentIndex + 1} z {totalCount} wynikow
                        </span>
                    </div>
                    <div className="header-right">
                        <span className="registration-date">
                            Data dodania: {formatDate(selectedOrganizer.registrationDate || selectedOrganizer.pendingRequestedAt)}
                        </span>
                    </div>
                </div>

                <div className="organizer-detail-content">
                    <div className="company-header-section">
                        <div className="company-info">
                            <div className="company-id-name">
                                <span className="company-id">(ID {selectedOrganizerId})</span>
                                <h2 className="company-name">{getDisplayValue('companyName')}</h2>
                                {reviewTitle && (
                                    <span className="organizer-review-badge">
                                        {reviewTitle}{isPendingDataChange ? ` - ${changedFieldsCount} pol` : ''}
                                    </span>
                                )}
                            </div>
                            <button
                                className={`statistics-btn ${requiresReview ? 'disabled' : ''}`}
                                onClick={handleStatistics}
                                disabled={requiresReview}
                                type="button"
                            >
                                <ChartIcon className="btn-icon" />
                                <span>Statystyki</span>
                            </button>
                        </div>
                    </div>

                    <div className="detail-tabs-nav">
                        <div className="tabs-container">
                            <button
                                className={`tab-btn ${activeTab === 'basic' ? 'active' : ''}`}
                                onClick={() => setActiveTab('basic')}
                                type="button"
                            >
                                Dane podstawowe
                            </button>
                            <button
                                className={`tab-btn ${activeTab === 'description' ? 'active' : ''}`}
                                onClick={() => setActiveTab('description')}
                                type="button"
                            >
                                Opis
                            </button>
                        </div>
                    </div>

                    {activeTab === 'basic' && (
                        <div className="tab-content basic-data">
                            <div className="data-section">
                                {renderDataRow('ID', selectedOrganizerId)}
                            </div>

                            <div className="data-section">
                                {renderReviewRow('Nazwa firmy', 'companyName', getBaseValue, getDisplayValue, isFieldChanged)}
                                {renderReviewRow('NIP', 'nip', getBaseValue, getDisplayValue, isFieldChanged)}
                                <div className={`data-row ${isFieldChanged('contactEmail') ? 'pending-change-row' : ''}`}>
                                    <div className="data-row-inner contact-row">
                                        <div className="data-label">Kontakt</div>
                                        <div className="data-value">{getDisplayValue('contactEmail')}</div>
                                        {isFieldChanged('contactEmail') && <span className="change-badge">Nowe dane</span>}
                                        <button className="password-reset-btn" onClick={handlePasswordReset} type="button">
                                            Wiadomosc z resetem hasla
                                        </button>
                                    </div>
                                </div>
                                {renderReviewRow('Telefon', 'contactPhone', getBaseValue, getDisplayValue, isFieldChanged)}
                            </div>

                            <div className="data-section">
                                <div className="section-header">
                                    {renderDataRow('Adres', '')}
                                </div>
                                <div className="address-details">
                                    {renderReviewRow('Ulica', 'street', getBaseValue, getDisplayValue, isFieldChanged)}
                                    <div className="data-row two-column">
                                        <div className={`data-row-inner ${isFieldChanged('postalCode') ? 'pending-change-row-inline' : ''}`}>
                                            <div className="data-label">Kod pocztowy</div>
                                            <div className="data-value">{getDisplayValue('postalCode')}</div>
                                            {isFieldChanged('postalCode') && <span className="change-badge">Nowe dane</span>}
                                        </div>
                                        <div className={`data-row-inner ${isFieldChanged('city') ? 'pending-change-row-inline' : ''}`}>
                                            <div className="data-label">Miejscowosc</div>
                                            <div className="data-value">{getDisplayValue('city')}</div>
                                            {isFieldChanged('city') && <span className="change-badge">Nowe dane</span>}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'description' && (
                        <div className="tab-content description-data">
                            <div className="data-section">
                                <div className={`data-row description-row ${isFieldChanged('description') ? 'pending-change-row' : ''}`}>
                                    <div className="data-row-inner">
                                        <div className="data-label">Opis dzialalnosci</div>
                                        <div className="data-value description-text">
                                            {getDisplayValue('description') || 'Brak opisu dzialalnosci.'}
                                        </div>
                                        {isFieldChanged('description') && <span className="change-badge">Nowe dane</span>}
                                    </div>
                                </div>
                            </div>

                            <div className="data-section">
                                {renderLinkRow('Link spolecznosciowy', 'socialLink', getBaseValue, getDisplayValue, isFieldChanged)}
                                {renderLinkRow('Strona WWW', 'siteLink', getBaseValue, getDisplayValue, isFieldChanged)}
                            </div>
                        </div>
                    )}
                </div>

                <div className="organizer-detail-footer">
                    <div className="footer-buttons">
                        {requiresReview ? (
                            <>
                                <button
                                    className="action-btn outline morski"
                                    onClick={handleRejectPending}
                                    disabled={isDecisionLoading}
                                    type="button"
                                >
                                    <CloseCircleIcon className="btn-icon" />
                                    <span>{isPendingDataChange ? 'Odrzuc zmiany' : 'Odrzuc'}</span>
                                </button>
                                <button
                                    className="action-btn filled"
                                    onClick={handleAcceptPending}
                                    disabled={isDecisionLoading}
                                    type="button"
                                >
                                    <CheckIcon className="btn-icon" />
                                    <span>{isPendingDataChange ? 'Zaakceptuj zmiany' : 'Zaakceptuj'}</span>
                                </button>
                            </>
                        ) : (
                            <>
                                <button className="action-btn outline danger" onClick={handleDelete} type="button">
                                    <TrashIcon className="btn-icon" />
                                    <span>Usun</span>
                                </button>
                                <button className="action-btn filled" onClick={handleEdit} type="button">
                                    <EditIcon className="btn-icon" />
                                    <span>Edytuj</span>
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}

const normalizeDisplayValue = (value) => {
    if (value === null || value === undefined || value === '') {
        return '-';
    }

    return String(value);
};

const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '-';

    return date.toLocaleDateString('pl-PL', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).replace(/\//g, '-');
};

const getAddressParts = (address) => {
    if (!address) return { street: '-', zipCode: '-', city: '-' };

    const parts = address.split(',').map((part) => part.trim());
    const street = parts[0] || '-';

    if (parts[1]) {
        const cityParts = parts[1].trim().split(' ');
        const zipCode = cityParts[0] || '-';
        const city = cityParts.slice(1).join(' ') || '-';
        return { street, zipCode, city };
    }

    return { street, zipCode: '-', city: '-' };
};

const renderDataRow = (label, value) => (
    <div className="data-row">
        <div className="data-row-inner">
            <div className="data-label">{label}</div>
            <div className="data-value">{value || '-'}</div>
        </div>
    </div>
);

const renderReviewRow = (label, field, getBaseValue, getDisplayValue, isFieldChanged) => {
    const changed = isFieldChanged(field);
    return (
        <div className={`data-row ${changed ? 'pending-change-row' : ''}`}>
            <div className="data-row-inner">
                <div className="data-label">{label}</div>
                <div className="data-value">
                    {changed && <span className="previous-value">{getBaseValue(field)}</span>}
                    <span>{getDisplayValue(field)}</span>
                </div>
                {changed && <span className="change-badge">Nowe dane</span>}
            </div>
        </div>
    );
};

const renderLinkRow = (label, field, getBaseValue, getDisplayValue, isFieldChanged) => {
    const changed = isFieldChanged(field);
    const value = getDisplayValue(field);
    const href = value && value !== '-' && value.startsWith('http') ? value : `https://${value}`;

    return (
        <div className={`data-row ${changed ? 'pending-change-row' : ''}`}>
            <div className="data-row-inner">
                <div className="data-label">{label}</div>
                <div className="data-value">
                    {changed && <span className="previous-value">{getBaseValue(field)}</span>}
                    {value && value !== '-' ? (
                        <a href={href} target="_blank" rel="noopener noreferrer" className="link-value">
                            {value}
                        </a>
                    ) : (
                        <span className="no-value">-</span>
                    )}
                </div>
                {changed && <span className="change-badge">Nowe dane</span>}
            </div>
        </div>
    );
};

export default OrganizerDetailPanel;
